import React, { useMemo } from 'react';
import { Select } from 'antd';
import { CsSelect } from 'components/antd-extension/CsSelect';
import { QueryType } from 'components/commons/IssueCrashFilter/IssueCrashFilterExUtil';
import { ze } from 'utils/zhEn';
import { isNotNullish } from 'utils/nullish';

/**
 * IcfInNotInMultiSelect：IssueCrashFilterEx 里通用的"多选 + 包含/不包含切换"过滤值输入组件。
 *
 * 左边是一个"包含/不包含"的下拉（对应 queryType=TERMS 时的 not=false/true），
 * 右边是一个带搜索的多选 Select（对应 terms）。适用于所有"多选枚举值、且希望支持反选"的字段，
 * 例如 CPU 架构、channelId、bundleId 等。
 *
 * fieldValue 结构：{ queryType: 'TERMS', not?: boolean, terms?: any[] }
 * 通过 onChangeFieldValue 向上回传新的 fieldValue。
 */
const IcfInNotInMultiSelect = ({
  isReadOnly,
  fieldValue,
  onChangeFieldValue,
  options,
  placeholder,
}) => {
  const SearchMethod = useMemo(() => ({
    IN: 'IN',
    NOT_IN: 'NOT_IN',
  }), []);

  const SearchMethodToInfo = useMemo(() => ({
    [SearchMethod.IN]: {
      queryType: QueryType.TERMS,
      not: false,
    },
    [SearchMethod.NOT_IN]: {
      queryType: QueryType.TERMS,
      not: true,
    },
  }), [SearchMethod.IN, SearchMethod.NOT_IN]);

  const QUERY_TYPE_OPTIONS = useMemo(() => [
    { name: ze('包含', 'Contains'), value: SearchMethod.IN },
    { name: ze('不包含', 'Not Contains'), value: SearchMethod.NOT_IN },
  ].map(x => ({
    ...x,
    label: x.name,
  })), [SearchMethod.IN, SearchMethod.NOT_IN]);

  const findSearchMethod = (queryType, not) => {
    const SearchMethodInfoEntries = Object.entries(SearchMethodToInfo);
    const foundEntry = SearchMethodInfoEntries.find(([k, v]) => {
      return v.queryType === queryType && v.not === Boolean(not);
    });
    return isNotNullish(foundEntry) ? foundEntry[0] : null;
  };

  const {
    queryType: nullableQueryType,
    not,
    terms,
  } = fieldValue || {};

  const queryType = nullableQueryType || QueryType.TERMS;
  let searchMethodValue = findSearchMethod(queryType, not);
  if (!searchMethodValue) {
    searchMethodValue = SearchMethod.IN;
  }

  return (
    <div style={{ display: 'flex' }}>
      <Select
        style={{ width: '120px', flex: '0 0 auto' }}
        options={QUERY_TYPE_OPTIONS}
        optionFilterProp='name'
        optionLabelProp='name'
        value={searchMethodValue}
        onChange={(v) => {
          if (isReadOnly) {
            return;
          }
          const newInfo = SearchMethodToInfo[v];
          onChangeFieldValue({ queryType: newInfo.queryType, not: newInfo.not, terms });
        }}
      />
      <CsSelect
        style={{ width: '100%', fontSize: '14px' }}
        mode='multiple'
        allowClear
        showSearch
        optionFilterProp='label'
        options={options}
        placeholder={placeholder}
        value={terms}
        onChange={(v) => {
          if (isReadOnly) {
            return;
          }
          onChangeFieldValue({ queryType, not, terms: v });
        }}
      />
    </div>
  );
};

export default IcfInNotInMultiSelect;
